/**
 *
 * Integrations Wizard
 *
 */

import React, { memo, useEffect } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Clients } from '@ariksa/notification';
import { Box, Stack } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { IdentityProviderName } from 'api/auth/api.pb';
import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { AddRepository } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/AddRepository';
import { Tableau } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/Tableau';
import { EditTableau } from 'containers/Setup/Integrations/Components/IntegrationsWizard/DataSources/Components/Tableau/EditTableau';
import { CreateEmailGroup } from 'containers/Setup/Integrations/Components/IntegrationsWizard/EmailGroup/CreateEmailGroup';
import { EditEmailGroup } from 'containers/Setup/Integrations/Components/IntegrationsWizard/EmailGroup/EditEmailGroup';
import { CreateJira } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Jira/Components/CreateJira';
import { EditJira } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Jira/Components/EditJira';
import { ServicenowWizard } from 'containers/Setup/Integrations/Components/IntegrationsWizard/ServiceNow/Components/ServicenowWizard';
import { AddElasticForSIEM } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SIEM/Elastic/AddElasticForSIEM';
import { EditElasticForSIEM } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SIEM/Elastic/EditElasticForSIEM';
import { CreateSlack } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Slack/Components/CreateSlack';
import { EditSlack } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Slack/Components/EditSlack';
import { CreateSMS } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SMS/CreateSMS';
import { EditSMS } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SMS/EditSMS';
import { SSOAddProvider } from 'containers/Setup/Integrations/Components/IntegrationsWizard/SSO/SSOAddProvider';
import { CreateTeams } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Teams/Components/CreateTeams';
import { EditTeams } from 'containers/Setup/Integrations/Components/IntegrationsWizard/Teams/Components/EditTeams';
import { getFormTitle } from 'containers/Setup/Integrations/Components/util';
import { actions } from 'containers/Setup/Integrations/slice';
import { actions as setupActions } from 'containers/Setup/slice';

export const IntegrationsWizard = memo(() => {
  const params = useParams<{
    provider: string;
    id: string;
  }>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!params.id && !params.provider) return;
    if (
      params.provider === Clients.Email ||
      params.provider === Clients.Jira ||
      params.provider === Clients.Servicenow ||
      params.provider === Clients.Slack ||
      params.provider === Clients.Teams ||
      params.provider === Clients.Sns ||
      params.provider === Clients.Elasticsearch
    )
      dispatch(actions.getClientDetailsById({ q: { uuid: params.id! } }));
  }, [dispatch, params.id, params.provider]);

  useEffect(() => {
    if (!params.id && !params.provider) return;
    if (
      params.provider === CloudProviders.Gitlab ||
      params.provider === CloudProviders.Github ||
      params.provider === CloudProviders.Bitbucket ||
      params.provider === 'tableau'
    )
      dispatch(
        setupActions.getCloudAccountDetails({ q: { uuid: params.id! } }),
      );
  }, [params.id, params.provider, dispatch]);

  const getComponent = () => {
    switch (params?.provider) {
      case CloudProviders.Gitlab:
      case CloudProviders.Github:
      case CloudProviders.Bitbucket:
        return <AddRepository />;
      case 'tableau':
        return !!params.id ? <EditTableau /> : <Tableau />;
      case Clients.Jira:
        return !!params.id ? <EditJira /> : <CreateJira />;
      case Clients.Servicenow:
        return <ServicenowWizard />;
      case Clients.Slack:
        return !!params.id ? <EditSlack /> : <CreateSlack />;
      case Clients.Teams:
        return !!params.id ? <EditTeams /> : <CreateTeams />;
      case Clients.Email:
        return !!params.id ? <EditEmailGroup /> : <CreateEmailGroup />;
      case Clients.Sns:
        return !!params.id ? <EditSMS /> : <CreateSMS />;
      case Clients.Elasticsearch:
        return !!params.id ? <EditElasticForSIEM /> : <AddElasticForSIEM />;
      case IdentityProviderName.Okta:
      case IdentityProviderName.Google:
      case IdentityProviderName.Microsoft:
        return <SSOAddProvider />;
    }
  };

  return (
    <Card>
      <Stack spacing={8} h="full" w="full" pl={32}>
        <Box>
          <PageHeaderWithIcon
            label={
              (!!params.id ? 'Update ' : 'Add ') +
              getFormTitle(params?.provider)
            }
            iconType={params?.provider}
            iconFilled={params.provider === Clients.Email}
          />
        </Box>
        <Box h="full" w="full" pl={2}>
          {getComponent()}
        </Box>
      </Stack>
    </Card>
  );
});
